import './form-select.scss';
import Choices from 'choices.js';

class FormSelect {
    constructor ($element, options) {
        const defaults = {
            initAttr: 'data-formselect'
        };

        this.settings = Object.assign({}, defaults, options);
        this.randomId = Math.floor(Math.random() * 10000000);

        // DOM Elements
        this.$formSelect = $element;
        this.$dropdown = null;

        if (!this.$formSelect.classList.contains('initialized')) {
            this.initialize();
        }
    }

    initialize () {
        this.$formSelect.classList.add('initialized');

        const choicesID = this.$formSelect.id;
        const multipleChoices = this.$formSelect.dataset.formselectMultiple === 'true';
        const hasSearch = this.$formSelect.classList.contains('has--search');

        this.formselect = new Choices(this.$formSelect, {
            closeDropdownOnSelect: !multipleChoices,
            allowHTML: true,
            removeItems: true,
            removeItemButton: multipleChoices,
            renderSelectedChoices: 'always',
            placeholder: true,
            placeholderValue: null,
            labelId: choicesID,
            searchEnabled: hasSearch,
            searchChoices: hasSearch,
            position: (multipleChoices) ? 'bottom' : 'bottom',
            searchPlaceholderValue: window.translations.choices.searchPlaceholderValue,
            noResultsText: window.translations.choices.noResultsText,
            noChoicesText: '',
            itemSelectText: '',
            singleModeForMultiSelect: multipleChoices,
            shouldSort: false,
            callbackOnCreateTemplates: function (strToEl, escapeForTemplate) {
                const itemSelectText = this.config.itemSelectText;
                const allowHTML = this.config.allowHTML;

                if (!hasSearch) {
                    return {
                        item: function ({ classNames }, data) {
                            // Items choices.js creates itself (e.g. the placeholder item
                            // rendered after all active items were removed) carry no
                            // backing option element. The placeholder class is required:
                            // choices.js removes the placeholder item by that class.
                            const $element = data.element;
                            let additionalElementBefore = '';

                            if ($element && $element.dataset.elementBefore) {
                                additionalElementBefore = ' ' + $element.dataset.elementBefore;
                            }

                            return strToEl(`
                            <div
                                class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable} ${data.selected ? classNames.selectedState : ''} ${data.placeholder ? classNames.placeholder : ''}"
                                data-item
                                data-id="${data.id}"
                                data-value="${escapeForTemplate(allowHTML, data.value)}"
                                ${data.disabled ? 'aria-disabled="true"' : ''}
                                ${hasSearch ? 'role="option"' : ''}
                            >
                                <span>${escapeForTemplate(allowHTML, additionalElementBefore)}${escapeForTemplate(allowHTML, data.label)}</span>
                            </div>
                        `);
                        },

                        choice: function ({ classNames }, data) {
                            // Choices added programmatically (setChoices) carry no
                            // backing option element.
                            const $element = data.element;
                            let additionalElementBefore = '';

                            if ($element && $element.dataset.elementBefore) {
                                additionalElementBefore = ' ' + $element.dataset.elementBefore;
                            }

                            return strToEl(`
                            <div
                                class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable} ${data.selected ? classNames.selectedState : ''}"
                                data-select-text="${itemSelectText}"
                                data-choice
                                ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}
                                data-id="${data.id}"
                                data-value="${escapeForTemplate(allowHTML, data.value)}"
                                ${data.groupId > 0 ? 'role="treeitem"' : 'role="option"'}
                            >
                                <span>${escapeForTemplate(allowHTML, additionalElementBefore)}${escapeForTemplate(allowHTML, data.label)}</span><svg aria-hidden="true" role="none" width="24" height="24" class="icon icon--is-check"><use xlink:href="#icon-check"></use></svg>
                            </div>
                        `);
                        }
                    };
                } else {
                    return [];
                }
            }
        });

        window.addEventListener('select.enable', (e) => {
            if (e.detail.$context === this.$formSelect) {
                this.formselect.enable();
            }
        });

        window.addEventListener('select.disable', (e) => {
            if (e.detail.$context === this.$formSelect) {
                if (this.$formSelect.value === '') {
                    this.formselect.disable();
                    this.formselect.removeActiveItems(1);
                } else {
                    this.$formSelect.value = '';
                    this.formselect.disable();
                    this.formselect.setChoices([{ value: '', label: window.translations.choices.placeholder, selected: true }], 'value', 'label', false);
                }
            }
        });

        const $choicesInner = this.$formSelect.closest('.choices__inner');
        const $choices = $choicesInner?.parentElement;
        this.$dropdown = $choices.querySelector('.choices__list--dropdown');
        const $searchListBox = $choices?.querySelector('.choices__list--single[role="listbox"]');

        if ($searchListBox && hasSearch) {
            $searchListBox.setAttribute('aria-label', 'Search list');
        }

        if ($choices && multipleChoices !== true) {
            const $select = $choices.querySelector('select');
            const ariaLabel = $select.getAttribute('aria-label');

            $choices.setAttribute('role', 'combobox');
            $choices.setAttribute('aria-controls', `${this.randomId}-listbox`);

            if (ariaLabel !== null) {
                $choices.setAttribute('aria-label', $select.getAttribute('aria-label'));
                $choices.removeAttribute('aria-labelledby');
            }
        }

        const autoCloseSelect = () => {
            if ($choices.classList.contains('is-open')) {
                $choices.parentElement.click();
                $choices.blur();
            }
        };

        if (multipleChoices) {
            const $choicesListMultiple = $choices.querySelector('.choices__list--multiple');
            const $choicesList = $choices.querySelector('.choices__list--dropdown');
            const $choicesSearch = $choices.querySelector('input[type="search"]');

            if (choicesID != null) {
                $choices.setAttribute('aria-controls', choicesID);
                $choicesList.id = choicesID;

                if ($choicesListMultiple.querySelectorAll('.choices__item').length <= 0) {
                    $choicesListMultiple.removeAttribute('role');
                } else {
                    $choicesListMultiple.setAttribute('aria-label', window.translations.filter.choicesSelectedList + this.$formSelect.getAttribute('aria-label'));
                }

                if ($choicesSearch) {
                    $choicesSearch.setAttribute('tabindex', '-1');
                    $choicesSearch.setAttribute('aria-label', window.translations.filter.formFilterSelectInputSearch + this.$formSelect.getAttribute('aria-label'));
                }
            }

            $choices.setAttribute('tabindex', '0');

            if (choicesID != null) {
                $choices.setAttribute('aria-controls', choicesID);
                this.$dropdown.id = choicesID;

                if ($choicesSearch && hasSearch === false) {
                    $choicesSearch.remove();
                }

                // deselect items from dropdown
                this.$dropdown.addEventListener('click', (event) => {
                    const $choiceItem = event.target.closest('[data-id]');

                    if ($choiceItem) {
                        this.formselect.removeActiveItemsByValue($choiceItem.dataset.value);
                    }
                });

                // close on tab out - because we can not tab through the options we use this event to close the dropdown
                $choices.addEventListener('keydown', (e) => {
                    if (e.code === 'Tab' || e.code === 'Tabs') {
                        autoCloseSelect();
                    }
                });

                // close multi choice dropdown
                $choices.onclick = () => {
                    autoCloseSelect();
                };
            }
        }

        if (this.$dropdown && this.$dropdown.firstElementChild) {
            this.$dropdown.firstElementChild.setAttribute('role', 'listbox');
            this.$dropdown.firstElementChild.id = `${this.randomId}-listbox`;
            this.$dropdown.removeAttribute('id');
        }

        window.addEventListener('customSelectChange', (event) => {
            if (event.detail.select === this.$formSelect) {
                const optionsData = event.detail.options;
                let currentValue = this.$formSelect.value;

                if (optionsData) {
                    Object.entries(optionsData).forEach(([key, option]) => {
                        if (option.disabled && option.value === currentValue) {
                            currentValue = 'select';
                        }
                    });

                    Object.entries(optionsData).forEach(([key, option]) => {
                        this.updateOptionState(option.value, option.disabled, option.value === currentValue);
                    });

                    this.syncGroupVisibility();
                }
            }
        });

        window.addEventListener('reset.filter', (e) => {
            this.formselect.removeActiveItems();
        });
    }

    // choices.js renders optgroups as a heading followed by its options as
    // siblings, so a heading whose options are all disabled (hidden via CSS)
    // would stay visible on its own - hide it alongside them.
    syncGroupVisibility () {
        this.$dropdown.querySelectorAll('[data-group]').forEach(($group) => {
            let $sibling = $group.nextElementSibling;
            let hasVisibleOption = false;

            while ($sibling !== null && !$sibling.hasAttribute('data-group')) {
                if ($sibling.getAttribute('aria-disabled') !== 'true') {
                    hasVisibleOption = true;
                    break;
                }

                $sibling = $sibling.nextElementSibling;
            }

            $group.hidden = !hasVisibleOption;
        });
    }

    updateOptionState (value, disabled, select = false) {
        const $option = Array.from(this.$formSelect.options).find(opt => opt.value === value);

        if ($option) {
            $option.disabled = disabled;

            if (select && !disabled) {
                $option.selected = true;
                this.$formSelect.value = value;
            }

            const $relatedChoice = this.$dropdown.querySelector(`[data-value="${$option.value}"]`);
            if ($relatedChoice) {
                $relatedChoice.setAttribute('aria-disabled', $option.disabled);
                if ($option.selected) {
                    $relatedChoice.classList.add('is-selected');
                } else {
                    $relatedChoice.classList.remove('is-selected');
                }
            }
        }
    }
}

export { FormSelect };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$formSelect = $context.querySelectorAll('[data-formselect]');
        for (let i = 0; i < $$formSelect.length; i++) {
            const $formSelect = $$formSelect[i];
            const formSelectAPI = new FormSelect($formSelect);
            $formSelect.formselectAPI = formSelectAPI;
        }
    }
});
